import {PtrnError} from '..';
import {
   ptrnValidarCampoEditableFn,
   PtrnCampoEditableBase,
   PtrnState,
   ptrnTransformarFn,
   PtrnCampoValidarResult,
   PtrnDataInput,
   PtrnAccionPametroAuxiliar,
   PtrnResultadoProcesarCampo
} from '../../patronTypes';
import {
   PtrnProcesarCommonOpts,
   PtrnTransformarCampoFn,
   PtrnValidarCampoFn
} from './ptrnProcesarTypes';
import {ValidateState} from 'lib.common';
import {PtrnMaestroDetalleConfigObj} from '../../hooks/usePtrnMaestroDetalleEstado/usePtrnEstadoMaestroDetalleTypes';
import {COD_ERR} from 'lib.common/dist/errorHandler';
export class PtrnProcesarUtilsCfg {
   static getValidCfg(
      cfg: PtrnCampoEditableBase | undefined,
      {campoNombre, origen}: PtrnProcesarCommonOpts
   ) {
      if (!cfg) {
         throw new PtrnError(
            `Ptrn->${origen}: Error al leer configuracion del estado. No existe configuracion para el campo ${campoNombre}`
         );
      }
      return cfg;
   }

   static getValidCfgMD(
      cfgMDObj: PtrnMaestroDetalleConfigObj | undefined,
      {campoNombre, origen, pathState}: PtrnProcesarCommonOpts & {pathState: string[]}
   ) {
      if (!cfgMDObj) {
         throw new PtrnError(
            `Ptrn->${origen}: Error al leer configuracion del estado. No existe configuracion para el campo ${campoNombre}`
         );
      }
      let currentMDCfg = cfgMDObj;

      // Recorremos `path` para llegar al objeto anidado deseado
      pathState.forEach((p) => {
         if (!currentMDCfg.detalles) {
            return;
         }

         if (currentMDCfg.detalles[p]) {
            currentMDCfg = currentMDCfg.detalles[p];
         }
      });

      return currentMDCfg;
   }

   static execValidarCfg(
      fn: ptrnValidarCampoEditableFn | undefined,
      newValue: any,
      estadoLocal: PtrnState,
      estadoGlobal: any
   ): PtrnCampoValidarResult {
      if (!fn) {
         return PtrnProcesarUtilsCfg.NoErrResult();
      }

      return fn(newValue, estadoLocal, estadoGlobal) as PtrnCampoValidarResult;
   }

   static execTransformarCfg(fn: ptrnTransformarFn | undefined, currValue: any, estado: PtrnState) {
      if (!fn) {
         return currValue;
      }

      return fn(currValue, estado);
   }

   static execValidarCampoFn(
      fn: PtrnValidarCampoFn | undefined,
      valor: any,
      cfg: PtrnCampoEditableBase,
      estado: PtrnState
   ): PtrnCampoValidarResult {
      if (!fn) {
         return PtrnProcesarUtilsCfg.NoErrResult();
      }
      return fn(valor, cfg, estado);
   }

   static execTransformarCampoFn(
      fn: PtrnTransformarCampoFn | undefined,
      valor: any,
      cfg: PtrnCampoEditableBase,
      estado: PtrnState
   ) {
      if (!fn) {
         return valor;
      }
      return fn(valor, cfg, estado);
   }

   static execAfterProcesarCampo(input: {
      resultadoProcesarValor: PtrnResultadoProcesarCampo;
      campoConfig: PtrnCampoEditableBase;
      onSetError: () => void;
      onSetNewValue: () => void;
      newState: PtrnDataInput;
      auxParam: PtrnAccionPametroAuxiliar;
   }) {
      const {valorProcesado, gravedadMaxima} = input.resultadoProcesarValor;
      if (
         gravedadMaxima === ValidateState.ERROR &&
         valorProcesado.errores &&
         valorProcesado.errores.length > 0
      ) {
         input.onSetError();
      } else {
         if (input.campoConfig.afterOnChange) {
            input.campoConfig.afterOnChange(input.newState, input.auxParam);
         }
         input.onSetNewValue();
      }
   }

   static gerarquiaGravedad: Record<ValidateState, number> = {
      [ValidateState.SUCCESS]: 0,
      [ValidateState.INFO]: 1,
      [ValidateState.WARNING]: 2,
      [ValidateState.ERROR]: 3
   };

   static getMaxGravedad(...errors: ValidateState[]): ValidateState {
      return errors.reduce(
         (highest, current) =>
            PtrnProcesarUtilsCfg.gerarquiaGravedad[current] >
            PtrnProcesarUtilsCfg.gerarquiaGravedad[highest]
               ? current
               : highest,
         ValidateState.SUCCESS
      );
   }

   static ErrValidar(msg: string) {
      return {
         codeValidate: COD_ERR.E422,
         estado: ValidateState.ERROR,
         msg
      };
   }
   static WarningValidar(msg: string) {
      return {
         codeValidate: COD_ERR.E422,
         estado: ValidateState.WARNING,
         msg
      };
   }
   static NoErrResult(): PtrnCampoValidarResult {
      return {
         errores: [],
         gravedadMaxima: ValidateState.SUCCESS
      };
   }
}

export class PtrnProcesarValidacionesComunes {
   static hasAnyCharacter(str: string, chars?: string[] | RegExp): boolean {
      if (!chars) {
         return false;
      }

      if (Array.isArray(chars)) {
         return chars.some((char) => str.includes(char));
      }

      return chars.test(str);
   }

   static validadorDigitosEnteros(valor: string, maxDigitosEnteros: number): boolean {
      // Removemos el signo negativo para la validación
      const valorAbsoluto = valor.startsWith('-') ? valor.slice(1) : valor;

      // Obtenemos la parte entera
      const parteEntera = valorAbsoluto.split('.')[0];

      // Validamos la cantidad de dígitos enteros
      return parteEntera.length <= maxDigitosEnteros;
   }

   static validadorDigitosDecimales(valor: string, maxDigitosDecimales: number): boolean {
      // Removemos el signo negativo para la validación
      const valorAbsoluto = valor.startsWith('-') ? valor.slice(1) : valor;

      // Obtenemos la parte decimal, si existe
      const parteDecimal = valorAbsoluto.split('.')[1];

      // Validamos la cantidad de dígitos decimales
      return !parteDecimal || parteDecimal.length <= maxDigitosDecimales;
   }

   static esMayor(value: string | number, maxValue: number | undefined): boolean {
      if (maxValue != undefined) {
         return Number(value) > maxValue;
      } else {
         return false;
      }
   }

   static esMenor(value: string | number, minValue: number | undefined): boolean {
      if (minValue != undefined) {
         return Number(value) < minValue;
      } else {
         return false;
      }
   }
}
